import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import HomePage from './pages/HomePage';
import DocPage from './pages/DocPage';
import NotFoundPage from './pages/NotFoundPage';
import DocsLayout from './components/DocsLayout';
import { LOCALES, type Locale } from './i18n';

const SUPPORTED_LANGS = new Set<string>(Object.values(LOCALES));

function isLocale(value: string): value is Locale {
  return SUPPORTED_LANGS.has(value);
}

/**
 * React Router v6 preserves the window scroll position across route changes,
 * which makes a fresh doc page show up mid-scroll when you navigate from a
 * page where you had scrolled down. Reset to the top on every pathname change.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/:lang/*" element={<LangGate />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

/**
 * Validates the :lang URL segment. Uses useParams so the component is
 * reactive to client-side navigation (window.location.pathname is not).
 */
function LangGate() {
  const { lang = '' } = useParams<{ lang: string }>();
  if (!isLocale(lang)) return <Navigate to="/" replace />;
  return (
    <DocsLayout lang={lang}>
      <DocPage lang={lang} />
    </DocsLayout>
  );
}
